import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { FiAlertTriangle } from "react-icons/fi";

interface WarningDialogProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const variantStyles = {
  danger: {
    iconBg: "bg-red-500/10",
    iconColor: "text-red-500",
    confirmBtn: "bg-red-600 hover:bg-red-700",
  },
  warning: {
    iconBg: "bg-yellow-500/10",
    iconColor: "text-yellow-500",
    confirmBtn: "bg-yellow-600 hover:bg-yellow-700",
  },
  info: {
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-500",
    confirmBtn: "bg-blue-600 hover:bg-blue-700",
  },
};

const WarningDialog = ({
  open,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "warning",
  isLoading = false,
  onConfirm,
  onCancel,
}: WarningDialogProps) => {
  const styles = variantStyles[variant];

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[1000]" onClose={onCancel}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              className="w-full max-w-md transform rounded-2xl bg-primary-light border border-primary-subtle shadow-2xl px-8 py-8 text-center 
             align-middle transition-all"
            >
              {/* Icon */}
              <div className="flex items-center justify-center mb-4">
                <div className={`p-3 rounded-full ${styles.iconBg}`}>
                  <FiAlertTriangle className={`w-6 h-6 ${styles.iconColor}`} />
                </div>
              </div>

              {/* Title */}
              <Dialog.Title className="text-lg font-semibold text-center text-neutral">
                {title}
              </Dialog.Title>

              {/* Description */}
              {description && (
                <p className="mt-2 text-sm text-neutral-medium text-center">
                  {description}
                </p>
              )}

              {/* Actions */}
              <div className="mt-8 flex items-center justify-center gap-4">
                <button
                  onClick={onCancel}
                  disabled={isLoading}
                  className="min-w-[110px] px-5 py-2.5 rounded-xl border border-primary-subtle 
               text-neutral-medium hover:bg-primary-subtle 
               transition-all duration-200 disabled:opacity-50"
                >
                  {cancelText}
                </button>

                <button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="min-w-[110px] px-5 py-2.5 rounded-xl 
               bg-gradient-to-r from-[#7C3AED] to-[#6366F1] 
               hover:opacity-90 text-white font-medium
               transition-all duration-200 
               disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Deleting..." : confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WarningDialog;
